/**
 * The running voice session's status, for UI outside the session's host.
 *
 * The session lives in VoiceModeHost, mounted beside the routes, while its
 * controls live in the composer. A small subscribable store connects them
 * without Redux: the mic level changes twenty times a second, and only the
 * component that draws it should re-render.
 */
import { useEffect, useSyncExternalStore } from "react";

import type { VoiceNotice, VoiceSessionState } from "./voiceSessionMachine.ts";

export interface VoiceStatus {
  /** "idle" whenever voice mode is off. */
  state: VoiceSessionState;
  notice: VoiceNotice | null;
  /** Live microphone level, 0..1. */
  level: number;
  /**
   * How many places on screen can show the session (the orb and its
   * controls). At zero the host shows them itself, so an open microphone is
   * never left without a visible way to end it.
   */
  surfaces: number;
}

const IDLE: VoiceStatus = {
  state: "idle",
  notice: null,
  level: 0,
  surfaces: 0,
};

let status: VoiceStatus = IDLE;
let interrupt: () => void = () => {};
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function publishVoiceStatus(next: Partial<VoiceStatus>): void {
  status = { ...status, ...next };
  listeners.forEach((listener) => listener());
}

/** Re-renders only when the selected value changes. */
export function useVoiceStatus<T>(select: (status: VoiceStatus) => T): T {
  return useSyncExternalStore(subscribe, () => select(status));
}

/** Registers the calling component as a place the session is shown. */
export function useVoiceSurface(present: boolean): void {
  useEffect(() => {
    if (!present) return;
    publishVoiceStatus({ surfaces: status.surfaces + 1 });
    return () => publishVoiceStatus({ surfaces: status.surfaces - 1 });
  }, [present]);
}

export function registerVoiceInterrupt(fn: () => void): void {
  interrupt = fn;
}

/** Stops what Tally is saying or working on, and goes back to listening. */
export function interruptVoice(): void {
  interrupt();
}

/** True while there is a response to stop. */
export function isVoiceBusy(state: VoiceSessionState): boolean {
  return state === "thinking" || state === "speaking";
}

/** Test seam. */
export function __resetVoiceStatusForTests(): void {
  // Keep mounted surfaces: tests reset between renders, not between mounts.
  status = { ...IDLE, surfaces: status.surfaces };
  interrupt = () => {};
}
